"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { fetchStockTransfers, addStockTransferToInventory } from "../../../../api/Inventory/StockTransferApi";

import styles from "../css/stockTransfer.module.css";
import Button from "../../../../components/Button";
import Status from "../../../../components/Status";
import StockTransferContent from "../components/StockTransferContent";
import SuccessNotification from "../../../../components/SuccessNotification";

export default function StockTransfer() {
  const { user } = useAuth();
  const roleId = Number(user?.roleid);
  const [rows, setRows] = useState([]);

  // Helper: format yyyy-mm-dd to dd/mm/yyyy for UI display only
  const formatDateDMY = (yyyyMmDd) => {
    const s = String(yyyyMmDd || '').trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}/${m[2]}/${m[1]}`;
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeSegment, setActiveSegment] = useState("Front Shop");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;
  const [showSuccess, setShowSuccess] = useState(false);

  const isApproved = (selectedRow?.status || '').toString().toLowerCase() === 'approved';

  // Prevent background scrolling when the details modal is open
  useEffect(() => {
    if (!isModalOpen) return;

    const originalOverflow = document.body.style.overflow || '';
    const originalHtmlOverflow = document.documentElement.style.overflow || '';
    const rootElement = document.getElementById('root');
    const originalRootOverflow = rootElement ? (rootElement.style.overflow || '') : '';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    if (rootElement) rootElement.style.overflow = 'hidden';

    // Also block wheel/touch scroll events at the window level while the modal is open
    const blockScroll = (e) => {
      e.preventDefault();
    };

    window.addEventListener('wheel', blockScroll, { passive: false });
    window.addEventListener('touchmove', blockScroll, { passive: false });

    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      if (rootElement) rootElement.style.overflow = originalRootOverflow;
      window.removeEventListener('wheel', blockScroll, { passive: false });
      window.removeEventListener('touchmove', blockScroll, { passive: false });
    };
  }, [isModalOpen]);

  // Load all stock transfer requests from backend
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const data = await fetchStockTransfers();
        if (!isMounted) return;

        // Normalize keys for table consumption
        const normalized = (data || []).map((row) => ({
          // Preserve all backend-provided fields for detailed View More usage
          ...row,
          // Normalize core table fields/aliases
          drugName: row.drugName ?? row["Product Name"] ?? "",
          type: row.type ?? row["Type"] ?? "",
          currentQty: row.currentQty ?? row["Current Quantity"] ?? 0,
          requestedQty: row.requestedQty ?? row["Requested Quantity"] ?? 0,
          transferTo: row.transferTo ?? row["Transfer To"] ?? "",
          transferFrom: row.transferFrom ?? row["Transferred from"] ?? row["Transfered from"] ?? "",
          status: row.status ?? row["Status"] ?? "Pending",
          requestDate: row.requestDate ?? row["Request Date"] ?? "",
          department: row.department ?? row["department"] ?? "",
        }));

        setRows(normalized);
      } catch (err) {
        console.error("Failed to load stock transfers", err);
        setRows([]);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAddToInventory = async () => {
    if (!selectedRow) return;
    try {
      if (selectedRow.requestId) {
        await addStockTransferToInventory(selectedRow.requestId);
      }
      // Optimistically remove the row from the local list (backend now filters by toinventory=false)
      setRows((prev) => prev.filter((r) => r !== selectedRow));
      setShowSuccess(true);
    } catch (err) {
      console.error("Failed to add stock transfer to inventory", err);
    } finally {
      setIsModalOpen(false);
      setSelectedRow(null);
    }
  };

  const firstColHeader =
    activeSegment === "Dispensary"
      ? "Drug Name"
      : activeSegment === "Clinic"
      ? "Item Name"
      : "Product Name";

  const columns = [
    { header: firstColHeader, accessor: "drugName" },
    { header: "Type", accessor: "type" },
    { header: "Current Quantity", accessor: "currentQty" },
    { header: "Requested Quantity", accessor: "requestedQty" },
    { header: "Transfer To", accessor: "transferTo" },
    { header: "Transfered from", accessor: "transferFrom" },
    { header: "Status", accessor: "status", render: (value) => <Status status={value} variant="process" /> },
    { header: "Request Date", accessor: "requestDate", render: (value) => formatDateDMY(value) },
    {
      header: "Action",
      accessor: "action",
      render: (_, row) => (
        <Button
          variant="primary"
          className={styles.viewBtn}
          onClick={() => {
            setSelectedRow(row);
            setIsModalOpen(true);
          }}
        >
          View More
        </Button>
      ),
    },
  ];

  const userBranchCode = user?.branchcode ? String(user.branchcode) : null;
  const filteredData = rows.filter((r) => {
    const matchesBranch = !userBranchCode || String(r.toBranchCode || "") === userBranchCode;
    return matchesBranch && r.department === activeSegment;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSegment]);

  // If Clinic user, force Clinic view and remove tabs
  useEffect(() => {
    if (roleId === 6 && activeSegment !== "Clinic") {
      setActiveSegment("Clinic");
    }
  }, [roleId, activeSegment]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Hide the "Type" column for Dispensary and OTC views to match the design
  const visibleColumns =
    activeSegment === "Clinic"
      ? columns
      : columns.filter((c) => c.accessor !== "type");

  return (
    <>
      <StockTransferContent
        roleId={roleId}
        activeSegment={activeSegment}
        setActiveSegment={setActiveSegment}
        visibleColumns={visibleColumns}
        pageData={pageData}
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        isModalOpen={isModalOpen}
        selectedRow={selectedRow}
        isApproved={isApproved}
        onCloseModal={() => setIsModalOpen(false)}
        onAddToInventory={handleAddToInventory}
      />
      {showSuccess && (
        <SuccessNotification
          message="Successfully added to inventory!"
          onClose={() => setShowSuccess(false)}
        />
      )}
    </>
  );
}
